/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lookbook, SavedOutfit } from '../types';
import { XIcon, ChevronLeftIcon, ChevronRightIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';

interface MagazineLookbookProps {
    lookbook: Lookbook;
    savedOutfits: SavedOutfit[];
    onClose: () => void;
}

const MagazineLookbook: React.FC<MagazineLookbookProps> = ({ lookbook, savedOutfits, onClose }) => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(0);

    const outfitsInLookbook = lookbook.outfitIds
        .map(id => savedOutfits.find(o => o.id === id))
        .filter((o): o is SavedOutfit => o !== undefined);

    // Page 0: Cover/Narrative
    // Page 1...N: Outfits (1 or 2 per page)

    const pages = [
        { type: 'cover' },
        ...outfitsInLookbook.map(o => ({ type: 'outfit', outfit: o }))
    ];

    const handleNext = () => {
        if (currentPage < pages.length - 1) setCurrentPage(currentPage + 1);
    };

    const handlePrev = () => {
        if (currentPage > 0) setCurrentPage(currentPage - 1);
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
            >
                <button onClick={onClose} className="absolute top-6 right-6 p-2 text-white/50 hover:text-white transition-colors" aria-label={t('common_close')}>
                    <XIcon className="w-8 h-8" />
                </button>

                <div className="relative w-full max-w-5xl h-[85vh] bg-stone-100 rounded-sm shadow-2xl overflow-hidden flex">
                    {/* Magazine Content */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentPage}
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -50 }}
                            transition={{ duration: 0.5, ease: "easeInOut" }}
                            className="w-full h-full"
                        >
                            {pages[currentPage].type === 'cover' && (
                                <div className="w-full h-full flex flex-col md:flex-row">
                                    <div className="w-full md:w-1/2 h-1/2 md:h-full relative overflow-hidden">
                                        <img src={lookbook.lookbookImageUrl || (outfitsInLookbook[0]?.previewImageUrl || '')} alt="Cover" className="absolute inset-0 w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-black/20" />
                                        <div className="absolute top-10 left-10 text-white z-10 mix-blend-overlay opacity-80">
                                            <h1 className="text-6xl md:text-8xl font-serif tracking-widest uppercase mb-2">STYLE</h1>
                                        </div>
                                        <div className="absolute bottom-10 left-10 text-white z-20">
                                            <h2 className="text-3xl font-serif">{lookbook.name}</h2>
                                        </div>
                                    </div>
                                    <div className="w-full md:w-1/2 h-1/2 md:h-full p-8 md:p-16 flex flex-col justify-center bg-stone-50 overflow-y-auto">
                                        <h3 className="text-amber-800 text-sm tracking-widest uppercase mb-6">{t('magazine_editorial', 'Editorial')}</h3>
                                        <p className="font-serif text-gray-800 leading-relaxed text-lg whitespace-pre-line first-letter:text-5xl first-letter:font-bold first-letter:mr-3 first-letter:float-left">
                                            {lookbook.editorialNarrative || 'A timeless collection curated just for you.'}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {pages[currentPage].type === 'outfit' && (
                                <div className="w-full h-full p-8 md:p-16 flex flex-col md:flex-row gap-8 items-center justify-center bg-stone-100">
                                    <div className="w-full md:w-1/2 h-full flex items-center justify-center">
                                        <img src={(pages[currentPage] as any).outfit.previewImageUrl} alt="Outfit preview" className="max-h-full max-w-full object-contain shadow-xl" />
                                    </div>
                                    <div className="w-full md:w-1/2 space-y-6">
                                        <h2 className="text-3xl font-serif text-gray-900 border-b border-gray-300 pb-4">{(pages[currentPage] as any).outfit.name}</h2>
                                        <p className="text-gray-600 font-serif italic text-lg line-clamp-3">{(pages[currentPage] as any).outfit.description || 'Elevate your wardrobe with this exquisite ensemble.'}</p>

                                        {/* Shop the story section */}
                                        <div className="mt-8">
                                            <h4 className="text-xs tracking-widest uppercase text-gray-500 mb-4">{t('magazine_shop_the_story', 'Shop the Story')}</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {((pages[currentPage] as any).outfit.layer.garments || []).map((g: any, i: number) => (
                                                    <span key={i} className="text-sm bg-white border border-gray-200 px-4 py-2 hover:bg-stone-50 transition-colors uppercase tracking-wider text-gray-800">
                                                        {t(g.nameKey)}
                                                    </span>
                                                ))}
                                                {((pages[currentPage] as any).outfit.layer.accessories || []).map((a: any, i: number) => (
                                                    <span key={i} className="text-sm bg-white border border-gray-200 px-4 py-2 hover:bg-stone-50 transition-colors uppercase tracking-wider text-gray-800">
                                                        {t(a.nameKey)}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        {lookbook.relatedRetailItems && lookbook.relatedRetailItems.length > 0 && (
                                            <div className="mt-8 border-t border-gray-300 pt-6">
                                                <div className="flex gap-4 overflow-x-auto pb-4 custom-scrollbar">
                                                    {lookbook.relatedRetailItems.map((item, idx) => (
                                                        <a key={idx} href={item.productUrl} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 w-24 group">
                                                            <img src={item.imageUrl} alt={item.name} className="w-full h-24 object-cover mb-2" />
                                                            <p className="text-[10px] font-medium text-gray-900 line-clamp-1 group-hover:underline">{item.name}</p>
                                                            <p className="text-[10px] text-gray-500">{item.price}</p>
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </AnimatePresence>

                    {/* Navigation */}
                    {currentPage > 0 && (
                        <button onClick={handlePrev} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/80 hover:bg-white rounded-full shadow-lg transition-transform hover:scale-110" aria-label={t('magazine_prev', 'Previous page')}>
                            <ChevronLeftIcon className="w-6 h-6 text-stone-800" />
                        </button>
                    )}
                    {currentPage < pages.length - 1 && (
                        <button onClick={handleNext} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/80 hover:bg-white rounded-full shadow-lg transition-transform hover:scale-110" aria-label={t('magazine_next', 'Next page')}>
                            <ChevronRightIcon className="w-6 h-6 text-stone-800" />
                        </button>
                    )}

                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                        {pages.map((_, i) => (
                            <div key={i} className={`h-1 transition-all ${i === currentPage ? 'w-8 bg-stone-800' : 'w-2 bg-stone-300'}`} />
                        ))}
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default MagazineLookbook;
